import { useState, useEffect, useRef, useCallback } from 'react';

interface UseProgressiveListOptions {
  pageSize?: number;
  /**
   * When this value changes, visible count resets to the first page.
   * Pass your search/filter params here — NOT the items array itself.
   * Resetting on the items array causes spurious resets on every store upsert
   * (e.g. a rating change triggering a reset while the user is mid-scroll).
   */
  resetKey?: unknown;
  /**
   * If true, the hook returns a containerRef that should be attached to the
   * scroll container. The IntersectionObserver then uses it as the root,
   * so the sentinel is detected within the container's viewport rather than
   * the page viewport. Use this for modals and bounded scroll areas.
   * Default: false (uses page viewport).
   */
  useScrollContainer?: boolean;
  rootMargin?: string;
}

interface UseProgressiveListResult<T> {
  visibleItems: T[];
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Attach to the scrollable container when `useScrollContainer` is true.
   * Unused when using page-level scroll.
   */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Progressively reveals items as the user scrolls, without rendering them all
 * upfront. Resets to the first page when `resetKey` changes.
 *
 * Two modes:
 * - Page scroll (default): sentinel fires when it enters the page viewport.
 * - Container scroll: pass `useScrollContainer: true` and attach `containerRef`
 *   to the scrollable element.
 */
export function useProgressiveList<T>(
  items: T[],
  options?: UseProgressiveListOptions
): UseProgressiveListResult<T> {
  const pageSize = options?.pageSize ?? 50;
  const resetKey = options?.resetKey;
  const useScrollContainer = options?.useScrollContainer ?? false;
  const rootMargin = options?.rootMargin ?? '200px';

  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset to first page when filters/search change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setVisibleCount(pageSize); }, [resetKey]);

  const setupObserver = useCallback(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const root = useScrollContainer ? containerRef.current : null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisibleCount((n) => Math.min(n + pageSize, items.length));
        }
      },
      { root, rootMargin }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [items.length, pageSize, useScrollContainer, rootMargin]);

  useEffect(() => setupObserver(), [setupObserver]);

  return {
    visibleItems: items.slice(0, visibleCount),
    sentinelRef,
    containerRef,
  };
}
